import { useTranslation } from '@repo/i18n'
import type {
	InfiniteData,
	UseInfiniteQueryResult
} from '@tanstack/react-query'
import { XIcon } from 'lucide-react'
import {
	createContext,
	type Ref,
	useContext,
	useEffect,
	useRef,
	useState
} from 'react'
import { AsyncPickerResults } from '#commons/async-picker-results'
import { Badge } from '#components/badge'
import { Button } from '#components/button'
import {
	Combobox,
	ComboboxChip,
	ComboboxChips,
	ComboboxChipsInput,
	ComboboxClear,
	ComboboxContent,
	ComboboxInput,
	ComboboxTrigger,
	ComboboxValue,
	useComboboxAnchor
} from '#components/combobox'
import { InputGroup, InputGroupAddon } from '#components/input-group'
import type { VirtualizationApi } from '#hooks/use-virtualization'
import { cn } from '#lib/utils'

// Restored from react-starter-staging/commons/async-pickers.tsx.
export type AsyncPickerSource<T> = {
	getKey: (item: T) => string
	getLabel: (item: T) => string
	onOpenChange?: (open: boolean) => void
	placeholder?: string
	empty?: React.ReactNode
	renderOption?: (item: T) => React.ReactNode
	itemDisabled?: (item: T) => boolean
} & (
	| {
			query: UseInfiniteQueryResult<InfiniteData<T[]>>
			onSearchChange: (search: string) => void
	  }
	| { items: readonly T[]; isPending?: boolean; isError?: boolean }
)

interface AsyncPickerControlProps {
	id?: string
	name?: string
	disabled?: boolean
	required?: boolean
	'aria-label'?: string
	'aria-invalid'?: boolean
	'aria-describedby'?: string
}

interface AsyncPickerSingleRootProps<T> extends AsyncPickerControlProps {
	source: AsyncPickerSource<T>
	value: T | null
	onValueChange: (value: T | null) => void
	children: React.ReactNode
	searchInputValue?: boolean
}

interface AsyncPickerMultiRootProps<T> extends AsyncPickerControlProps {
	source: AsyncPickerSource<T>
	value: T[]
	onValueChange: (value: T[]) => void
	children: React.ReactNode
	searchInputValue?: boolean
	maxSelected?: number
}

type AsyncPickerRootProps<T> =
	| (AsyncPickerSingleRootProps<T> & { multiple?: false })
	| (AsyncPickerMultiRootProps<T> & { multiple: true })

const AsyncPickerContext = createContext<{
	results: React.ReactNode
	selection: {
		canSelectAll: boolean
		canClear: boolean
		onSelectAll: () => void
		onClear: () => void
	} | null
} | null>(null)

export function AsyncPickerRoot<T>(props: AsyncPickerRootProps<T>) {
	const [search, setSearch] = useState('')
	const listRef = useRef<VirtualizationApi>(null)
	const onSearchChange =
		'query' in props.source ? props.source.onSearchChange : undefined
	const lastEmittedSearch = useRef('')
	const [pinnedItems, setPinnedItems] = useState<T[]>([])
	const fetchedItems =
		'query' in props.source
			? (props.source.query.data?.pages.flat() ?? [])
			: props.source.items.filter((item) =>
					props.source
						.getLabel(item)
						.toLocaleLowerCase()
						.includes(search.trim().toLocaleLowerCase())
				)
	const visiblePinnedItems = search === '' ? pinnedItems : []
	const selectedItems = props.multiple
		? visiblePinnedItems
		: [props.value].filter((item): item is T => item !== null)

	const selectedKeys = new Set(selectedItems.map(props.source.getKey))
	const items = [
		...selectedItems,
		...fetchedItems.filter(
			(item) => !selectedKeys.has(props.source.getKey(item))
		)
	]

	const valueKeys = new Set(
		(props.multiple ? props.value : []).map(props.source.getKey)
	)
	const maxReached = Boolean(
		props.multiple &&
			props.maxSelected !== undefined &&
			props.value.length >= props.maxSelected
	)

	const isItemDisabled = (item: T) => {
		if (props.source.itemDisabled?.(item)) {
			return true
		}
		return maxReached && !valueKeys.has(props.source.getKey(item))
	}

	useEffect(() => {
		if (!onSearchChange || search === lastEmittedSearch.current) {
			return
		}

		const timeout = setTimeout(() => {
			onSearchChange(search)
			lastEmittedSearch.current = search
		}, 250)

		return () => clearTimeout(timeout)
	}, [onSearchChange, search])

	const resetSearch = () => {
		setSearch('')

		if (lastEmittedSearch.current !== '') {
			onSearchChange?.('')
		}

		lastEmittedSearch.current = ''
	}

	const handleValueChange = (value: T | T[] | null) => {
		if (
			props.required &&
			(value === null || (Array.isArray(value) && value.length === 0))
		) {
			return
		}
		if (props.multiple) {
			props.onValueChange(Array.isArray(value) ? value : [])
			return
		}

		props.onValueChange(Array.isArray(value) ? null : value)
	}

	const selectableItems = fetchedItems.filter(
		(item) =>
			!(valueKeys.has(props.source.getKey(item)) || isItemDisabled(item))
	)
	const unavailable =
		'query' in props.source
			? props.source.query.isFetching ||
				props.source.query.isPending ||
				props.source.query.isError
			: props.source.isPending || props.source.isError

	return (
		<AsyncPickerContext.Provider
			value={{
				selection: props.multiple
					? {
							canSelectAll:
								!(props.disabled || unavailable) &&
								selectableItems.length > 0,
							canClear:
								!(props.disabled || props.required) &&
								props.value.length > 0,
							onSelectAll: () => {
								const selected = Array.from(
									new Map(
										[
											...props.value,
											...selectableItems
										].map((item) => [
											props.source.getKey(item),
											item
										])
									).values()
								)
								props.onValueChange(
									props.maxSelected === undefined
										? selected
										: selected.slice(0, props.maxSelected)
								)
							},
							onClear: () => {
								props.onValueChange([])
							}
						}
					: null,
				results: (
					<AsyncPickerResults
						empty={props.source.empty}
						getKey={props.source.getKey}
						getLabel={props.source.getLabel}
						isItemDisabled={isItemDisabled}
						items={items}
						ref={listRef}
						renderOption={props.source.renderOption}
						source={props.source}
					/>
				)
			}}
		>
			<Combobox
				disabled={props.disabled}
				filter={null}
				id={props.id}
				inputValue={props.searchInputValue ? search : undefined}
				isItemEqualToValue={(a, b) =>
					props.source.getKey(a) === props.source.getKey(b)
				}
				items={items}
				itemToStringLabel={props.source.getLabel}
				itemToStringValue={props.source.getKey}
				multiple={props.multiple}
				name={props.name}
				onInputValueChange={setSearch}
				onItemHighlighted={(_item, details) => {
					if (details.reason !== 'pointer' && details.index >= 0) {
						listRef.current?.scrollToIndex(
							'vertical',
							details.index,
							{ align: 'auto' }
						)
					}
				}}
				onOpenChange={(open) => {
					props.source.onOpenChange?.(open)
					if (props.multiple) {
						setPinnedItems(open ? props.value : [])
					}
					if (!open) {
						resetSearch()
					}
				}}
				onValueChange={handleValueChange}
				required={props.required}
				value={props.value}
				virtualized
			>
				{props.children}
			</Combobox>
		</AsyncPickerContext.Provider>
	)
}

export function AsyncPickerList() {
	const context = useContext(AsyncPickerContext)

	if (!context) {
		throw new Error('AsyncPicker components must be used within Root')
	}

	return context.results
}

export function AsyncPickerSelectionActions(props: {
	selectAllLabel: string
	clearLabel: string
}) {
	const context = useContext(AsyncPickerContext)

	if (!context?.selection) {
		throw new Error(
			'AsyncPickerSelectionActions must be used within a multiple Root'
		)
	}

	return (
		<div className='flex shrink-0 items-center gap-1 border-b p-1'>
			<Button
				disabled={!context.selection.canSelectAll}
				onClick={context.selection.onSelectAll}
				size='sm'
				type='button'
				variant='ghost'
			>
				{props.selectAllLabel}
			</Button>
			<Button
				disabled={!context.selection.canClear}
				onClick={context.selection.onClear}
				size='sm'
				type='button'
				variant='ghost'
			>
				{props.clearLabel}
			</Button>
		</div>
	)
}

export function AsyncSelect<T>(
	props: AsyncPickerControlProps & {
		source: AsyncPickerSource<T>
		value: T | null
		onValueChange: (value: T | null) => void
		ref?: Ref<HTMLButtonElement>
	}
) {
	const anchor = useComboboxAnchor()
	const translation = useTranslation()

	return (
		<AsyncPickerRoot
			disabled={props.disabled}
			id={props.id}
			name={props.name}
			onValueChange={props.onValueChange}
			required={props.required}
			searchInputValue
			source={props.source}
			value={props.value}
		>
			<InputGroup ref={anchor}>
				<ComboboxTrigger
					aria-describedby={props['aria-describedby']}
					aria-invalid={props['aria-invalid']}
					aria-label={props['aria-label']}
					className={cn(
						'min-w-0 flex-1 justify-between',
						props.value !== null &&
							!props.required &&
							'[&>svg]:hidden'
					)}
					disabled={props.disabled}
					ref={props.ref}
					render={<Button variant='ghost' />}
				>
					<ComboboxValue>
						{(value: T | null) => {
							if (value !== null) {
								return props.source.getLabel(value)
							}

							return (
								<span className='text-muted-foreground'>
									{props.source.placeholder}
								</span>
							)
						}}
					</ComboboxValue>
				</ComboboxTrigger>
				{props.value !== null && !props.required && (
					<InputGroupAddon align='inline-end'>
						<ComboboxClear
							aria-label={translation.t(
								'shared:async_pickers.clear'
							)}
						/>
					</InputGroupAddon>
				)}
			</InputGroup>
			<ComboboxContent
				anchor={anchor}
				className='flex min-w-(--anchor-width) flex-col [&>[data-slot=input-group]]:shrink-0'
			>
				<ComboboxInput
					aria-label={
						props['aria-label'] ??
						props.source.placeholder ??
						translation.t('shared:async_pickers.search')
					}
					placeholder={props.source.placeholder}
					showTrigger={false}
				/>
				<AsyncPickerList />
			</ComboboxContent>
		</AsyncPickerRoot>
	)
}

export function AsyncCombobox<T>(
	props: AsyncPickerControlProps & {
		source: AsyncPickerSource<T>
		value: T | null
		onValueChange: (value: T | null) => void
		ref?: Ref<HTMLInputElement>
	}
) {
	const anchor = useComboboxAnchor()
	const translation = useTranslation()

	return (
		<AsyncPickerRoot
			disabled={props.disabled}
			id={props.id}
			name={props.name}
			onValueChange={props.onValueChange}
			required={props.required}
			source={props.source}
			value={props.value}
		>
			<div ref={anchor}>
				<ComboboxInput
					aria-describedby={props['aria-describedby']}
					aria-invalid={props['aria-invalid']}
					aria-label={props['aria-label']}
					className='w-full'
					disabled={props.disabled}
					placeholder={props.source.placeholder}
					ref={props.ref}
				>
					{!props.required && (
						<InputGroupAddon align='inline-end'>
							<ComboboxClear
								aria-label={translation.t(
									'shared:async_pickers.clear'
								)}
							/>
						</InputGroupAddon>
					)}
				</ComboboxInput>
			</div>
			<ComboboxContent anchor={anchor} className='flex flex-col'>
				<AsyncPickerList />
			</ComboboxContent>
		</AsyncPickerRoot>
	)
}

export function AsyncMultiSelect<T>(
	props: AsyncPickerControlProps & {
		source: AsyncPickerSource<T>
		value: T[]
		onValueChange: (value: T[]) => void
		maxSelected?: number
		ref?: Ref<HTMLInputElement>
	}
) {
	const anchor = useComboboxAnchor()
	const translation = useTranslation()

	return (
		<AsyncPickerRoot<T>
			disabled={props.disabled}
			id={props.id}
			maxSelected={props.maxSelected}
			multiple
			name={props.name}
			onValueChange={props.onValueChange}
			required={props.required}
			searchInputValue
			source={props.source}
			value={props.value}
		>
			<ComboboxTrigger
				aria-describedby={props['aria-describedby']}
				aria-invalid={props['aria-invalid']}
				aria-label={props['aria-label']}
				className={cn(
					'cursor-default flex-nowrap overflow-hidden',
					props.value.length > 0 && '[&>svg]:hidden'
				)}
				disabled={props.disabled}
				render={<ComboboxChips ref={anchor} />}
			>
				<div className='flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden'>
					{props.value.map((value) => (
						<ComboboxChip
							className='max-w-40 shrink-0'
							key={props.source.getKey(value)}
							showRemove={false}
						>
							<span className='min-w-0 truncate'>
								{props.source.getLabel(value)}
							</span>
							<Button
								aria-label={translation.t(
									'shared:async_pickers.remove',
									{ label: props.source.getLabel(value) }
								)}
								className='opacity-50 hover:opacity-100'
								disabled={props.disabled}
								onClick={() => {
									props.onValueChange(
										props.value.filter(
											(item) =>
												props.source.getKey(item) !==
												props.source.getKey(value)
										)
									)
								}}
								onMouseDown={(event) => event.stopPropagation()}
								size='icon-xs'
								variant='ghost'
							>
								<XIcon />
							</Button>
						</ComboboxChip>
					))}
					<ComboboxChipsInput
						aria-label={
							props['aria-label'] ??
							props.source.placeholder ??
							translation.t('shared:async_pickers.search')
						}
						className='min-w-8 flex-1 cursor-default'
						disabled={props.disabled}
						placeholder={
							props.value.length === 0
								? props.source.placeholder
								: undefined
						}
						ref={props.ref}
					/>
				</div>
				{props.value.length > 0 && (
					<Badge variant='outline'>
						{translation.t('shared:async_pickers.selected_count', {
							count: props.value.length
						})}
					</Badge>
				)}
				{props.value.length > 0 && (
					<ComboboxClear
						aria-label={translation.t('shared:async_pickers.clear')}
						onMouseDown={(event) => event.stopPropagation()}
					/>
				)}
			</ComboboxTrigger>
			<ComboboxContent
				anchor={anchor}
				className='flex min-w-(--anchor-width) flex-col [&>[data-slot=input-group]]:shrink-0'
			>
				<ComboboxInput
					aria-label={
						props['aria-label'] ??
						props.source.placeholder ??
						translation.t('shared:async_pickers.search')
					}
					placeholder={props.source.placeholder}
					showTrigger={false}
				/>
				<AsyncPickerList />
			</ComboboxContent>
		</AsyncPickerRoot>
	)
}
